import { useState, useCallback } from 'react'
import { get, post } from '../helper/network'
import API from '../helper/api'
import type {
  Plan, CurrentPlan, CreateOrderRequest, CreateOrderResponse,
  OrderItem, PaginatedResponse, PayChannel, PayStatus,
} from '../types'

export function usePlan() {
  const [plans, setPlans] = useState<Plan[]>([])
  const [currentPlan, setCurrentPlan] = useState<CurrentPlan | null>(null)
  const [orders, setOrders] = useState<OrderItem[]>([])
  const [orderTotal, setOrderTotal] = useState(0)
  const [orderPage, setOrderPage] = useState(0)
  const [orderTotalPages, setOrderTotalPages] = useState(0)
  const [loading, setLoading] = useState(false)

  const fetchPlans = useCallback(async () => {
    setLoading(true)
    try {
      const res: any = await get(API.GET_PLANS)
      if (res?.code === 200 && res.data) {
        setPlans(res.data)
      } else {
        throw new Error(res?.message || 'fetch plans failed')
      }
    } catch (e) {
      setLoading(false)
      throw e
    }
    setLoading(false)
  }, [])

  const fetchCurrentPlan = useCallback(async () => {
    try {
      const res: any = await get(API.GET_CURRENT_PLAN)
      if (res?.code === 200 && res.data) {
        setCurrentPlan(res.data)
      }
    } catch {}
  }, [])

  const fetchOrders = useCallback(async (page = 1, size = 10) => {
    try {
      const res: any = await get(API.GET_PAYMENT_ORDERS, { pageNum: page, pageSize: size })
      if (res?.code === 200 && res.data) {
        const data = res.data
        if (data.records && Array.isArray(data.records)) {
          // MyBatis-Plus 分页格式 (current 从1开始)
          setOrders(data.records)
          setOrderTotal(data.total || 0)
          setOrderPage(data.current ? data.current - 1 : 0)
          setOrderTotalPages(data.pages || 0)
        } else if (data.content && Array.isArray(data.content)) {
          // Spring Data 分页格式 (number 从0开始)
          setOrders(data.content)
          setOrderTotal(data.totalElements || 0)
          setOrderPage(data.number ?? 0)
          setOrderTotalPages(data.totalPages || 0)
        } else if (Array.isArray(data)) {
          setOrders(data)
          setOrderTotal(data.length)
          setOrderPage(0)
          setOrderTotalPages(1)
        }
      }
    } catch {}
  }, [])

  const createOrder = async (request: CreateOrderRequest): Promise<CreateOrderResponse | null> => {
    try {
      const res: any = await post(API.POST_CREATE_ORDER, request)
      if (res?.code === 200 && res.data) {
        return res.data
      }
    } catch {}
    return null
  }

  const pollPaymentStatus = async (orderNo: string): Promise<PayStatus | null> => {
    try {
      const res: any = await get(`${API.GET_PAYMENT_STATUS}/${orderNo}`)
      if (res?.code === 200) {
        return res.data as PayStatus
      }
    } catch {}
    return null
  }

  const fetchOrderDetail = async (orderNo: string): Promise<OrderItem | null> => {
    try {
      const res: any = await get(`${API.GET_ORDER_DETAIL}/${orderNo}`)
      if (res?.code === 200 && res.data) {
        return res.data as OrderItem
      }
    } catch {}
    return null
  }

  return {
    plans,
    currentPlan,
    orders,
    orderTotal,
    orderPage,
    orderTotalPages,
    loading,
    fetchPlans,
    fetchCurrentPlan,
    fetchOrders,
    fetchOrderDetail,
    createOrder,
    pollPaymentStatus,
  }
}
